import { Injectable } from "@angular/core";
import { TripDashboardTile } from "../models/tripDashboardTile";
import { TripSlas } from "../models/tripSlas";
import { DecimalPipe, DatePipe } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";
import { DbKeyValueDecimal } from "../models/dbKeyValueDecimal";
import { TelecomOperator } from "../models/telecomOperator";
import { environment } from "../../../../environments/environment";
import { BytesPipe } from "ngx-pipes";
import { isNumber } from "angular";
import { TripStats } from "../models/tripStats";
import * as FileSaver from "file-saver";
import { TripDurationPipe } from '../pipes/trip-duration.pipe';
@Injectable({
  providedIn: "root",
})
export class TripDashboardDataService {
  private _portalServiceClassNames: DbKeyValueDecimal[];

  constructor(
    private decimalPipe: DecimalPipe,
    private bytesPipe: BytesPipe,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private tripDuration: TripDurationPipe
  ) { }

  getTiles = (
    tripSlas: TripSlas,
    operatorConsumptions: DbKeyValueDecimal[]
  ): TripDashboardTile[] => {
    const isDashboardHideObis =
      tripSlas.dashboardConfiguration &&
      tripSlas.dashboardConfiguration.dashboardHideObis;
    let tiles: TripDashboardTile[] = [];
    tiles = [
      {
        key: "numberOfTrips",
        header: "I18N_DASHBOARD.ACTIVE_TRIPS",
        message: tripSlas.numberOfTrips.toString(),
        tooltip: "I18N_DASHBOARD.ACTIVE_TRIPS_TOOLTIP",
        icon: "train",
        bgColor: "success",
      },
      {
        key: "averageThroughput",
        header: "I18N_DASHBOARD.AVG_THROUGHPUT",
        message:
          this.decimalPipe.transform(
            tripSlas.averageThroughput / 1000,
            "1.2-2"
          ) + " Mbit/s",
        tooltip: "I18N_DASHBOARD.AVG_THROUGHPUT_TOOLTIP",
        icon: "train",
        bgColor: "success",
      },
      {
        key: "uniqueUsers",
        header: "I18N_DASHBOARD.TOTAL_INTERNET_USERS",
        message: tripSlas.uniqueUsers.toString(),
        tooltip: "I18N_DASHBOARD.TOTAL_INTERNET_USERS_TOOLTIP",
        icon: "users",
        bgColor: "success",
      },
      {
        key: "totalPortalUsers",
        header: "I18N_DASHBOARD.TOTAL_PORTAL_USERS",
        message: this.translate.instant("I18N_DASHBOARD.NOT_CONFIGURED"),
        tooltip: "I18N_DASHBOARD.TOTAL_PORTAL_USERS_TOOLTIP",
        icon: "train",
        hidden:
          tripSlas.dashboardConfiguration &&
          tripSlas.dashboardConfiguration.dashboardHidePortalUsersTile,
        bgColor: "success",
      },
      {
        key: "totalConsumption",
        header: "I18N_DASHBOARD.TOTAL_CONSUMPTION",
        message:
          operatorConsumptions &&
          this.bytesPipe
            .transform(
              operatorConsumptions.reduce(
                (acc, operator) => acc + operator.dbValue,
                0
              ),
              2
            )
            .toString(), // {{ toGigaByte(dashboard.totalConsumption) }} GB
        tooltip: "I18N_DASHBOARD.TOTAL_CONSUMPTION_TOOLTIP",
        icon: "database",
        bgColor: "success",
      },
      {
        key: "exceedPercentage",
        header: "I18N_DASHBOARD.FAIR_USE_EXCEEDED",
        message:
          this.decimalPipe.transform(tripSlas.exceedPercentage, "1.2-2") + "%",
        tooltip: "I18N_DASHBOARD.FAIR_USE_EXCEEDED_TOOLTIP",
        icon: "line-chart",
        hidden:
          tripSlas.dashboardConfiguration &&
          tripSlas.dashboardConfiguration.dashboardHidePortalUsersTile,
        bgColor: "success",
      },
      {
        key: "internetAvailability",
        header: "I18N_DASHBOARD.TRAIN_GROUND_CONNECTIVITY",
        message:
          tripSlas.internetAvailability === -1
            ? this.translate.instant("I18N_DASHBOARD.NOT_CONFIGURED")
            : this.decimalPipe.transform(
              tripSlas.internetAvailability,
              "1.2-2"
            ) + "%",
        tooltip: "I18N_DASHBOARD.TRAIN_GROUND_CONNECTIVITY_TOOLTIP",
        icon: "wifi",
        hidden:
          tripSlas.dashboardConfiguration &&
          tripSlas.dashboardConfiguration.dashboardHideTrainGroundConnectivity,
        bgColor: "primary",
      },
      {
        key: "portalAvailability",
        header: "I18N_DASHBOARD.TRAIN_BACKBONE_CONNECTIVITY",
        message:
          tripSlas.portalAvailability === -1
            ? this.translate.instant("I18N_DASHBOARD.NOT_CONFIGURED")
            : this.decimalPipe.transform(tripSlas.portalAvailability, "1.2-2") +
            "%",
        tooltip: "I18N_DASHBOARD.TRAIN_BACKBONE_CONNECTIVITY_TOOLTIP",
        icon: "info-circle",
        hidden:
          tripSlas.dashboardConfiguration &&
          tripSlas.dashboardConfiguration.dashboardHideBackboneAvailability,
        bgColor: "primary",
      },
      {
        key: "obisTotalMmcLifeSign",
        header: "I18N_DASHBOARD.TOTAL_MMC_LIFESIGN",
        message:
          tripSlas.obisTotalMmcLifeSign === -1
            ? this.translate.instant("I18N_DASHBOARD.NOT_CONFIGURED")
            : this.decimalPipe.transform(
              tripSlas.obisTotalMmcLifeSign,
              "1.2-2"
            ) + "%",
        tooltip: "I18N_DASHBOARD.TOTAL_MMC_LIFESIGN_TOOLTIP",
        icon: "heartbeat",
        hidden: isDashboardHideObis,
        bgColor: "primary",
      },
      {
        key: "obisTotalPsuLifeSign",
        header: "I18N_DASHBOARD.TOTAL_PSU_LIFESIGN",
        message:
          tripSlas.obisTotalPsuLifeSign === -1
            ? this.translate.instant("I18N_DASHBOARD.NOT_CONFIGURED")
            : this.decimalPipe.transform(
              tripSlas.obisTotalPsuLifeSign,
              "1.2-2"
            ) + "%",
        tooltip: "I18N_DASHBOARD.TOTAL_PSU_LIFESIGN_TOOLTIP",
        icon: "heartbeat",
        hidden: isDashboardHideObis,
        bgColor: "primary",
      },
      {
        key: "obisTotalMmdLifeSign",
        header: "I18N_DASHBOARD.TOTAL_MMD_LIFESIGN",
        message:
          tripSlas.obisTotalMmdLifeSign === -1
            ? this.translate.instant("I18N_DASHBOARD.NOT_CONFIGURED")
            : this.decimalPipe.transform(
              tripSlas.obisTotalMmdLifeSign,
              "1.2-2"
            ) + "%",
        tooltip: "I18N_DASHBOARD.TOTAL_MMD_LIFESIGN_TOOLTIP",
        icon: "heartbeat",
        hidden: isDashboardHideObis,
        bgColor: "primary",
      },
      {
        key: "obisMmdBacklight",
        header: "I18N_DASHBOARD.MMD_BACKLIGHT_STATUS",
        message:
          tripSlas.obisMmdBacklight === -1
            ? this.translate.instant("I18N_DASHBOARD.NOT_CONFIGURED")
            : this.decimalPipe.transform(tripSlas.obisMmdBacklight, "1.2-2") +
            "%",
        tooltip: "I18N_DASHBOARD.MMD_BACKLIGHT_STATUS_TOOLTIP",
        icon: "heartbeat",
        hidden: isDashboardHideObis,
        bgColor: "primary",
      },
      {
        key: "obisContentOk",
        header: "I18N_DASHBOARD.OBIS_CONTENT",
        message:
          tripSlas.obisContentOk === -1
            ? this.translate.instant("I18N_DASHBOARD.NOT_CONFIGURED")
            : this.decimalPipe.transform(tripSlas.obisContentOk, "1.2-2") + "%",
        tooltip: "I18N_DASHBOARD.OBIS_CONTENT_TOOLTIP",
        icon: "image",
        hidden: isDashboardHideObis,
        bgColor: "primary",
      },
      {
        key: "obisGlobalStatusOk",
        header: "I18N_DASHBOARD.OBIS_GLOBAL_STATUS",
        message:
          tripSlas.obisGlobalStatusOk === -1
            ? this.translate.instant("I18N_DASHBOARD.NOT_CONFIGURED")
            : this.decimalPipe.transform(tripSlas.obisGlobalStatusOk, "1.2-2") +
            "%",
        tooltip: "I18N_DASHBOARD.OBIS_GLOBAL_STATUS_TOOLTIP",
        icon: "plug",
        hidden: isDashboardHideObis,
        bgColor: "primary",
      },
    ];
    return tiles;
  };

  processOperatorConsumption = (
    operatorConsumptions: DbKeyValueDecimal[],
    operators: TelecomOperator[]
  ) => {
    return {
      colors: this._extractColors(
        operatorConsumptions.map((c) => c.dbKey),
        operators
      ),
      data: this._convertKeyValueToDataSet(
        operatorConsumptions,
        (value) => Math.round(value),
        null,
        (value) => this.bytesPipe.transform(value, 2)
      ),
    };
  };

  processExceededUsers = (
    exceededUsers: DbKeyValueDecimal[],
    operators: TelecomOperator[],
    portalServiceClassNames: DbKeyValueDecimal[]
  ) => {
    this._portalServiceClassNames = portalServiceClassNames;

    const exceededUserEntities = exceededUsers.reduce((acc, entry) => {
      let key = entry.dbKey;
      if (!isNumber(entry.dbKey)) {
        key = this._replaceClassNameById(entry);
      }
      if (acc[key]) {
        acc[key] += entry.dbValue;
      } else {
        acc[key] = {
          dbKey: key,
          dbValue: entry.dbValue,
        };
      }
      return acc;
    }, {});

    return {
      data: this._convertKeyValueToDataSet(
        Object.keys(exceededUserEntities).map(
          (key) => exceededUserEntities[key]
        ),
        null,
        this.replaceClassIdByName
      ),
      colors: this._extractColors(
        Object.keys(exceededUserEntities).map(
          (key) => exceededUserEntities[key] && exceededUserEntities[key].dbKey
        ),
        operators
      ),
    };
  };

  processSpreadUsers = (
    spreadUsers: DbKeyValueDecimal[],
    operators: TelecomOperator[],
    portalServiceClassNames: DbKeyValueDecimal[]
  ) => {
    this._portalServiceClassNames = portalServiceClassNames;
    return {
      data: this._convertKeyValueToDataSet(
        spreadUsers,
        null,
        this.replaceClassIdByName
      ),
      colors: this._extractColors(
        spreadUsers.map((u) => u.dbKey),
        operators
      ),
    };
  };

  processClassAvgConsumptions = (
    classAvgConsumptions: DbKeyValueDecimal[],
    operators: TelecomOperator[],
    portalServiceClassNames: DbKeyValueDecimal[]
  ) => {
    this._portalServiceClassNames = portalServiceClassNames;
    return {
      data: this._convertKeyValueToDataSet(
        classAvgConsumptions,
        (value) => parseFloat(this.bytesPipe.transform(value, 2).toString()),
        this.replaceClassIdByName
      ),
      colors: this._extractColors(
        classAvgConsumptions.map((c) => c.dbKey),
        operators
      ),
    };
  };

  processChartData = (
    operatorDailyPercentageConsumptions: DbKeyValueDecimal[],
    operators: TelecomOperator[],
    portalServiceClassNames: DbKeyValueDecimal[],
    formatValueFn?,
    formatKeyFn?
  ) => {
    this._portalServiceClassNames = portalServiceClassNames;
    operatorDailyPercentageConsumptions =
      operatorDailyPercentageConsumptions.sort((c1, c2) =>
        c1.timestamp > c2.timestamp ? 1 : -1
      );

    const data = this._extractDailyDataset(
      operatorDailyPercentageConsumptions,
      formatValueFn
        ? formatValueFn
        : (value: DbKeyValueDecimal) => Math.round(value.dbValue),
      formatKeyFn ? formatKeyFn : (value: DbKeyValueDecimal) => value.dbKey
    );

    const categories = this._extractCategories(
      operatorDailyPercentageConsumptions
    );

    const colors = this._extractColors(
      data.map((c) => c.name),
      operators
    );

    return {
      data,
      categories,
      colors,
    };
  };

  processQoEChartData = (data: DbKeyValueDecimal[], colors: any[]) => {
    const dataset = this._extractDailyDataset(
            data, 
            (entry) => [new Date(entry.timestamp).getTime(), this._formatQoESqiValue(entry)], 
            (entry) => this._getQoESqiName(entry), 
            (category, holder) => category === holder.name, 
            (value) => this._getQoESqiName(value), 
            true, 
            true);

    const chartData  = dataset.map((item) => {
              return {
                ...item,
                yAxis: this._getQoEAxisNumber(item.key),
                color: this._getQoEAxisColor(item.key, colors)
              }
    });
  
    return {
      data: chartData, 
      categories: []
    };
  }

  _extractColors = (dbKeys: string[], operators: TelecomOperator[]) => {
    return dbKeys.reduce((acc: string[], dbKey) => {
      let color: string;
      if (operators) {
        const currentOperator = operators.find(
          (operator) =>
            operator.network + " " + operator.iso.toUpperCase() === dbKey
        );
        color = currentOperator && currentOperator.color || this._findUnusedColor(acc);
      } else {
        color = this._findUnusedColor(acc);
      }
      acc = [...acc, color];
      return acc;
    }, []);
  };

  _findUnusedColor(reservedColors: string[]) {
    const defaultColors = environment.charts.defaultColors.operators;
    const unusedColors = reservedColors
      ? defaultColors.filter((color) => {
        return !reservedColors.find((reservedColor) => {
          return reservedColor === color;
        });
      })
      : defaultColors;
    return unusedColors[0];
  }

  _convertKeyValueToDataSet = (
    dbKeyValues: DbKeyValueDecimal[],
    formatValueFn,
    formatKeyFn?,
    formatLabelFn?
  ) => {
    return dbKeyValues.map((entry) => {
      const value = !formatValueFn
        ? entry.dbValue
        : formatValueFn(entry.dbValue);
      const key = !formatKeyFn ? entry.dbKey : formatKeyFn(entry);
      return {
        name: key,
        y: value,
        label: !formatLabelFn ? value : formatLabelFn(entry.dbValue),
      };
    });
  };

  _replaceClassNameById = (value: DbKeyValueDecimal) => {
    const portalClass = this._portalServiceClassNames.find(
      (className) => className.dbKey === value.dbKey
    );
    return portalClass
      ? portalClass.dbKey
      : this.translate.instant("I18N_DASHBOARD.NOT_CONFIGURED");
  };

  replaceClassIdByName = (value: DbKeyValueDecimal) => {
    const portalClass = this._portalServiceClassNames.find(
      (className) => className.dbKey === value.dbKey
    );
    return portalClass
      ? portalClass.dbValue
      : this.translate.instant("I18N_DASHBOARD.NOT_CONFIGURED");
  };

  _extractDailyDataset = (
    values: DbKeyValueDecimal[],
    formatValueFn?,
    formatKeyFn?,
    categoryMatchFn?,
    getCategoryFn?,
    ignoreEmptyValue?,
    isQoEChart?
  ) => {
    const categories = this._extractCategories(values, getCategoryFn);
    var dataset: any[] = [];

    for (let index = 0; index < values.length; index++) {
      const v = values[index];
      var categoryValues = new Map();
      categories.forEach(function (category) {
        categoryValues.set(category, []);
      });

      var holder = {
        name: formatKeyFn ? formatKeyFn(v) : v.dbKey,
        data: [],
        key: v.dbKey,
        categoryValues: categoryValues,
      };

      var existing = dataset.find((item) => item.name === holder.name);

      if (existing !== undefined) {
        holder = existing;
      } else {
        dataset = [...dataset, holder];
      }

      var wantedCategoryValue = getCategoryFn
        ? getCategoryFn(v)
        : this.datePipe.transform(v.timestamp, "dd-MM-yyyy");
      if (holder.categoryValues.get(wantedCategoryValue)) {
        holder.categoryValues.get(wantedCategoryValue).push(v);
      }
    }

    ignoreEmptyValue = ignoreEmptyValue ? ignoreEmptyValue : false;
    dataset.forEach(function (holder) {
      holder.categoryValues.forEach(function (value) {
        if (!value || value.length === 0) {
          if (!ignoreEmptyValue) {
            holder.data.push(0);
          }
        } else {
          if (isQoEChart) {
            value.forEach(function (vs) {
              holder.data.push(!formatValueFn ? vs.dbValue : formatValueFn(vs));
            });
          } else {
            // adding values of the same category for the same day
            var totalValue = value.reduce((acc, val) => acc + val.dbValue, 0);
            holder.data.push(
              !formatValueFn
                ? totalValue
                : formatValueFn({ dbValue: totalValue })
            );
          }
        }
      });
    });
    return dataset;
  };

  _extractCategories(values: DbKeyValueDecimal[], getValueFn?) {
    const categories = values
      .sort((v1, v2) => (v1.timestamp > v2.timestamp ? 1 : -1))
      .map((value) =>
        getValueFn
          ? getValueFn(value)
          : this.datePipe.transform(value.timestamp, "dd-MM-yyyy")
      );
    return [...new Set(categories)];
  }

  exportTrips = (tripStats: TripStats[]) => {
    const separator = ";"; // TODO should be obtained from locale
    let csvString = "";
    csvString += this.translate.instant('I18N_TRIP.ID') + separator;
    csvString +=this.translate.instant('I18N_TRIP.TRAIN_ID') + separator;
    csvString += this.translate.instant('I18N_TRIP.TRAIN') + separator;
    csvString += this.translate.instant('I18N_TRIP.START') + separator;
    csvString += this.translate.instant('I18N_TRIP.STOP') + separator;
    csvString += this.translate.instant('I18N_TRIP.DEPARTURE') + separator;
    csvString +=this.translate.instant('I18N_TRIP.ARRIVAL') + separator;
    csvString += this.translate.instant('I18N_TRIP.DURATION') + separator;
    csvString += this.translate.instant('I18N_TRIP.MAIN_TRIP') + separator;
    csvString += "\n";

    csvString = tripStats.reduce((acc, trip) => {
      acc += trip.id + separator;
      acc += trip.trainId + separator;
      acc += trip.officialId + separator;
      acc += trip.departureStation + separator;
      acc += trip.arrivalStation + separator;
      acc += this.datePipe.transform(trip.departure, 'dd/MM/yyyy  HH:mm') + separator;
      acc += this.datePipe.transform(trip.arrival, 'dd/MM/yyyy  HH:mm') + separator;
      acc += this.tripDuration.transform(trip.arrival - trip.departure) + separator;
      acc += trip.mainTrainTrip + separator;
      acc += "\n";
      return acc;
    }, csvString);

    const blob = new Blob([csvString], { type: "text/plain;charset=utf-8" });
    FileSaver.saveAs(
      blob,
      this.datePipe.transform(new Date(), "yyyy_MM_dd_HH_mm") + ".csv"
    );
  };

  _getQoESqiName = (entry) => {
    switch (entry.dbKey) {
      //1500,1501: bytes.
      case "1500":
        return this.translate.instant("I18N_DASHBOARD.CHART_DAILY_QOS_USER_KBPS_90");
      case "1501":
        return this.translate.instant("I18N_DASHBOARD.CHART_DAILY_QOS_USER_KBPS");
      case "1502":
        return this.translate.instant("I18N_DASHBOARD.CHART_DAILY_QOS_GOOGLE_90");
      case "1503":
        return this.translate.instant("I18N_DASHBOARD.CHART_DAILY_QOS_GOOGLE");
      case "208":
        return this.translate.instant("I18N_DASHBOARD.CHART_DAILY_QOS_LATENCY");
    }
  }

  _formatQoESqiValue = (entry: DbKeyValueDecimal) => {
    //1500,1501: bytes.
    var value = 0;
    switch (entry.dbKey) {
      case "1500":
      case "1501":
        //bytes
        value = Number(Math.round(parseFloat(entry.dbValue/1024 * 8 + 'e' + '1')) + 'e-' + '1');
        break;
      case "1502":
      case "1503":
        value = entry.dbValue;
        break;
      default:
        value = entry.dbValue;
    }
    return value;
  }

  _getQoEAxisNumber = (key: string) => {
    var value = 0;
    switch (key) {
      case "1500":
      case "1501":
        value = 0;
        break;
      case "1502":
      case "1503":
        value = 1;
        break;
      case "208":
        value = 2;
        break;
      default:
        value = 0;
    }
    return value;
  }

  _getQoEAxisColor = (key: string, colors: any[]) => {
    var value = 0;
    switch (key) {
      case "1500":
      case "1501":
        value = colors[2];
        break;
      case "1502":
      case "1503":
        value = colors[0];
        break;
      case "208":
        value = colors[1];
        break;
      default:
        value = colors[3];
    }
    return value;
  }
}
